import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import {
  injectMutation,
  injectQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom, map, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthSessionService } from '../../auth/services/auth-session.service';
import { SubdomainService } from '../../../core/services/subdomain.service';
import {
  normalizeDisclosureGrantsResponse,
  normalizeDisclosureRequestsResponse,
} from '../helpers/disclosure-scope.helper';
import { ParentIdentityService } from './parent-identity.service';
import {
  DisclosureGrant,
  DisclosureRequest,
  DisclosureRequestsResponse,
  DisclosureGrantsResponse,
  DossierScopeCatalogResponse,
} from '../models/disclosure.model';

@Injectable({ providedIn: 'root' })
export class ParentDisclosureService {
  #http = inject(HttpClient);
  #session = inject(AuthSessionService);
  #subdomain = inject(SubdomainService);
  #identity = inject(ParentIdentityService);
  #qc = inject(QueryClient);

  readonly enabled = computed(
    () =>
      this.#session.isAuthenticated() &&
      this.#subdomain.isFamilySubdomain() &&
      this.#identity.layerEnabled(),
  );

  readonly scopeCatalogQuery = injectQuery(() => ({
    queryKey: ['disclosure', 'scopes'],
    queryFn: () => this.fetchScopeCatalog(),
    enabled: this.enabled(),
    staleTime: 60_000,
  }));

  readonly pendingQuery = injectQuery(() => ({
    queryKey: ['disclosure', 'pending'],
    queryFn: () => this.fetchPending(),
    enabled: this.enabled(),
    staleTime: 15_000,
  }));

  readonly parentGrantsQuery = injectQuery(() => ({
    queryKey: ['disclosure', 'parent-grants'],
    queryFn: () => this.fetchParentGrants(),
    enabled: this.enabled(),
    staleTime: 15_000,
  }));

  readonly pendingCount = computed(
    () => this.pendingQuery.data()?.items?.length ?? 0,
  );

  readonly pending = computed(() => this.pendingQuery.data()?.items ?? []);

  readonly activeGrants = computed(
    () => this.parentGrantsQuery.data()?.items ?? [],
  );

  readonly activeGrantCount = computed(() => this.activeGrants().length);

  readonly approveMutation = injectMutation(() => ({
    mutationFn: (input: {
      requestId: string;
      password?: string;
      totpCode?: string;
    }) =>
      lastValueFrom(
        this.#http
          .post<DisclosureGrant>(
            `${environment.baseUrl}/user/parent/disclosure-requests/${encodeURIComponent(input.requestId)}/approve`,
            {
              password: input.password ?? '',
              totpCode: input.totpCode ?? '',
            },
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      void this.#qc.invalidateQueries({ queryKey: ['disclosure'] });
    },
  }));

  readonly denyMutation = injectMutation(() => ({
    mutationFn: (requestId: string) =>
      lastValueFrom(
        this.#http
          .post<DisclosureRequest>(
            `${environment.baseUrl}/user/parent/disclosure-requests/${encodeURIComponent(requestId)}/deny`,
            {},
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      void this.#qc.invalidateQueries({ queryKey: ['disclosure'] });
    },
  }));

  readonly revokeMutation = injectMutation(() => ({
    mutationFn: (grantId: string) =>
      lastValueFrom(
        this.#http
          .post<{ status?: boolean }>(
            `${environment.baseUrl}/user/disclosure-grants/${encodeURIComponent(grantId)}/revoke`,
            {},
          )
          .pipe(take(1)),
      ),
    onSuccess: () => {
      void this.#qc.invalidateQueries({ queryKey: ['disclosure'] });
    },
  }));

  async reloadPending(): Promise<DisclosureRequestsResponse> {
    const result = await this.pendingQuery.refetch();
    return result.data ?? { items: [] };
  }

  async reloadParentGrants(): Promise<DisclosureGrantsResponse> {
    const result = await this.parentGrantsQuery.refetch();
    return result.data ?? { items: [] };
  }

  private fetchPending(): Promise<DisclosureRequestsResponse> {
    return lastValueFrom(
      this.#http
        .get<DisclosureRequestsResponse>(
          `${environment.baseUrl}/user/parent/disclosure-requests/pending`,
        )
        .pipe(
          map((response) => normalizeDisclosureRequestsResponse(response)),
          take(1),
        ),
    );
  }

  private fetchParentGrants(): Promise<DisclosureGrantsResponse> {
    return lastValueFrom(
      this.#http
        .get<DisclosureGrantsResponse>(
          `${environment.baseUrl}/user/parent/disclosure-grants`,
        )
        .pipe(
          map((response) => normalizeDisclosureGrantsResponse(response)),
          take(1),
        ),
    );
  }

  private fetchScopeCatalog(): Promise<DossierScopeCatalogResponse> {
    return lastValueFrom(
      this.#http
        .get<DossierScopeCatalogResponse>(
          `${environment.baseUrl}/user/disclosure/scopes`,
        )
        .pipe(take(1)),
    );
  }
}
